<template>
    <template v-if="features['new-resource-card-in-collections']">
        <div class="collection-thumbnail" />
        <div class="collection-thumbnail__overlay">
            <div class="collection-thumbnail__info">
                <div class="collection-thumbnail__title">{{ t('resources.collection') }}</div>
                <div>
                    <span class="collection-thumbnail__label">{{ collectionEntries.reduce((sum, {amount}) => sum + amount, 0) }}</span>
                    <span class="collection-thumbnail__label">
                        {{ t('resources.resourcesCenter').toLowerCase() }}
                    </span>
                </div>
            </div>
        </div>
    </template>
    <template v-else>
        <NuxtLink v-slot="{href, navigate}" :to="collection.url!" custom>
            <div class="collection-thumbnail-deprecated" @click="navigate">
                <div>
                    <div class="heading">{{ t('resources.collection') }}</div>
                    <a :href="href">
                        <h3 v-if="collection.data?.title" class="collection-title">
                            {{ collection.data.title }}
                        </h3>
                    </a>
                </div>
                <div>
                    <div
                        v-for="[contentType, amount] in Object.entries(collectionNumbers).filter(([_, amount]) => amount > 0) as [
                            keyof typeof CUSTOM_TYPE_PLURALS,
                            any,
                        ][]"
                        :key="contentType"
                    >
                        <span class="number">{{ amount }}</span>
                        <span>{{ t(`resources.${amount > 1 ? CUSTOM_TYPE_PLURALS[contentType] : contentType}`) }}</span>
                    </div>
                </div>
            </div>
        </NuxtLink>
    </template>
</template>
<script lang="ts" setup>
    import {computed} from 'vue';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import type {CollectionDocument, CollectionDocumentData} from '@prismicio-types';
    import {createContentHelpers, useTranslation} from '#imports';
    import {useFeatureFlagsStore} from '~/stores/feature-flags.store';

    const {features} = useFeatureFlagsStore();

    type ContentTypeKey = keyof typeof CUSTOM_TYPE_PLURALS;
    type ResourceLabelKey = `resources.${ContentTypeKey | (typeof CUSTOM_TYPE_PLURALS)[ContentTypeKey]}`;

    const props = defineProps<{collection: CollectionDocument | FilledContentRelationshipField<'collection', string, CollectionDocumentData>}>();
    const {t} = useTranslation();
    const {getCollectionNumbers} = createContentHelpers();
    const collectionNumbers = getCollectionNumbers(props.collection);

    const CUSTOM_TYPE_PLURALS = {
        article: 'articles',
        successStories: 'successStories',
        guides: 'guides',
        events: 'events',
    } as const;

    const collectionEntries = computed(() =>
        (Object.entries(collectionNumbers) as [ContentTypeKey, number][])
            .filter(([, amount]) => amount > 0)
            .map(([contentType, amount]): {contentType: ContentTypeKey; amount: number; labelKey: ResourceLabelKey} => ({
                contentType,
                amount,
                labelKey: `resources.${amount > 1 ? CUSTOM_TYPE_PLURALS[contentType] : contentType}` as ResourceLabelKey,
            })),
    );
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    .collection-thumbnail-deprecated {
        background-color: var(--joy-color-primary-70);
        padding: var(--joy-core-spacing-6);
        border-radius: var(--joy-core-radius-3);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        background-image: url('~/assets/svg/cornerPill.svg');
        background-position: bottom right;
        background-repeat: no-repeat;
        background-size: 70%;
        transition: background-size var(--joy-transition-duration-default) var(--joy-transition-timing-function);

        * {
            color: var(--joy-color-neutral-0);
        }
        &:hover {
            background-size: 140%;
        }
        .heading {
            margin-bottom: var(--joy-core-spacing-4);
            font-size: var(--joy-font-size-primary-800);
            font-family: var(--joy-font-family-title);
            @media (max-width: $mobile-breakpoint) {
                font-size: var(--joy-font-size-primary-600);
            }
        }
        .collection-title {
            margin-bottom: var(--joy-core-spacing-12);
            @media (max-width: $mobile-breakpoint) {
                margin-bottom: var(--joy-core-spacing-7);
            }
        }
        .number {
            font-size: var(--joy-font-size-primary-800);
            margin-right: var(--joy-core-spacing-2);
        }
    }

    .collection-thumbnail {
        border: 1px solid var(--joy-color-primary-50);
        padding: var(--joy-core-spacing-6);
        border-radius: var(--joy-core-radius-3);
        background-image: url('../assets/svg/collection-background.svg');
        height: 220px;
        position: relative;
        overflow: hidden;
        width: 100%;
        background-repeat: no-repeat;
        background-size: cover;

        &__overlay {
            width: 100%;
            height: 100%;
            position: absolute;
            top: -3px;
            left: 0;
            background-color: var(--joy-color-neutral-70);
            z-index: 1;
            width: 220px;
            height: 72px;
            border-radius: 0 0 100px 0;
            transform: rotate(-4deg);
        }

        &__info {
            position: absolute;
            top: 15px;
            left: 25px;
            z-index: 3;
            color: var(--joy-color-neutral-0);
            transform: rotate(4deg);
        }

        &__title {
            font-size: var(--joy-font-size-primary-800);
            font-weight: var(--joy-font-weight-bold);
            font-family: var(--joy-font-family-title);
            line-height: 1;
        }

        &__label {
            color: var(--joy-color-primary-50);
            font-size: var(--joy-font-size-primary-300);
            margin-left: var(--joy-core-spacing-0);
        }
    }
</style>
